import { makeStyles } from '@material-ui/styles';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from '@tools/i18n';
import { GetServerSidePropsContext } from 'next';
import { Container, Button, Typography, Box } from '@material-ui/core';
import useUserStore from '@services/stores/UserStore';
import CelebrationSelector from '@pages/account/wishlists/create/CelebrationSelector';
import StandardWishlistConfigurator from '@pages/account/wishlists/create/StandardWishlistConfigurator';
import { CreateWishlist, WishlistType } from '@services/MyNewportService';
import { appDataCache } from '@services/ServerCacheManager';
import { useRouter } from 'next/router';
import Link from 'next/link';
import DateWishlistConfigurator from '@pages/account/wishlists/create/DateWishlistConfigurator';
import WeddingWishlistConfigurator from '@pages/account/wishlists/create/WeddingWishlistConfigurator';
import theme from '@components/theme';
import { TrackingContext } from '@components/providers/tracking';
import useApplicationStore from '@services/stores/ApplicationStore';
import MetaTitle from '@components/meta/title';


// Shared theming for celebration components
export const backgrounds = {
    wedding: {
        background: '#deeeec',
        image: 'url("/images/wishlist-assets/flowers/flower_wedding.png")',
        color: '#222'
    },
    birthday: {
        background: '#f6f2e5',
        image: 'url("/images/wishlist-assets/flowers/flower_2.png")',
        color: '#222'
    },
    move: {
        background: '#efefea',
        image: 'url("/images/wishlist-assets/flowers/flower_1.png")',
        color: '#222'
    },
    graduation: {
        background: '#efefea',
        image: 'url("/images/wishlist-assets/flowers/flower_2.png")',
        color: '#222'
    },
    baby: {
        background: '#b2c2d4',
        image: 'url("/images/wishlist-assets/flowers/flower_2.png")',
        color: '#fff'
    },
    christmas: {
        background: '#112617',
        image: 'url("/images/wishlist-assets/flowers/flower_xmas.png")',
        color: '#fff'
    },
    standard: {
        background: '#f1f1ed',
        image: 'url("/images/wishlist-assets/flowers/flower_1.png")',
        color: 'var(--gold-color)'
    },
};

export const useStyles = makeStyles({
    page: {
        '--gold-color': '#a09267',
        '--continueButton': '#749180',
        '--continueButtonColor': '#fff',
        '--flower': 'url("/images/wishlist-assets/flowers/flower_1.png")',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        minHeight: '90vh',
        margin: '0 auto',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        transition: 'background 0.5s ease',
        color: 'var(--gold-color)',
        fontWeight: 300,
        fontSize: 20,
        position: 'relative',
        padding: '3em 0 4em',
        '&:before': {
            maxWidth: '90%',
            width: 500,
            height: 150,
            content: '""',
            position: 'absolute',
            background: 'var(--flower)',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            pointerEvents: 'none',
            backgroundPosition: 'center bottom',
            transition: 'all 0.5s ease',
            bottom: 0,
            top: 'auto',
        },
    },
    pageContent: {
        maxWidth: '650px',
        padding: '0 33px 66px',
        display: 'flex',
        flexDirection: 'column',
        '& input': {
            background: '#fff',
        },
    },
    pageControls: {
        paddingTop: '0',
        margin: '0',
        display: 'flex',
        gap: '10px',
        justifyContent: 'center',
        fontFamily: 'Chronicle',
        '& button': {
            color: 'inherit'
        },
        '& > *': {
            pointerEvents: 'none',
            opacity: 0,
        },
        '& .show': {
            pointerEvents: 'initial',
            opacity: 1,
        }
    },
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'transparent',
        textAlign: 'center',
        '& fieldset': {
            padding: 0,
            margin: 0,
            border: 'none',
            width: '100%',
        }
    },
    title: {
        marginBottom: 32,
        fontFamily: 'Chronicle',
        '&.MuiTypography-subtitle1': {
            fontFamily: 'Futura-pt',
            margin: '2em auto -8px 0',
        },
    },
    description: {
        fontFamily: 'Chronicle',
        fontWeight: 200,
        fontSize: 25,
    },
    divider: {
        borderBottom: '1px solid var(--gold-color)',
        margin: '24px 0',
        width: '70%',
    },
    radioGroup: {
        width: '100%',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 20,
        // @ts-ignore
        [theme.breakpoints.down('xs')]: {
            gridTemplateColumns: '1fr',
            gap: 10,
        },
    },
    radioButton: {
        padding: 16,
        fontWeight: 600,
        borderRadius: 0,
        border: 'none',
        background: 'var(--gold-color)',
        cursor: 'pointer',
        outline: 'none',
        width: '100%',
        color: '#fff',
        textTransform: 'uppercase',
        '&:hover': {
            background: 'color-mix(in hsl, var(--gold-color) 85%, white)',
        },
        '&:focus-visible': {
            borderColor: '#333',
            boxShadow: '0 0 0 2px #00000040',
        },

    },
    selected: {
        position: 'relative',
        '&:before': {
            content: '""',
            color: '#fff',
            inset: '5px 10px',
            position: 'absolute',
            borderBottom:' 3px solid #d0be85',
        },
    },
    continueButton: {
        maxWidth: '200px',
        width: '100%',
        padding: 10,
        background: 'var(--continueButton)',
        color: 'var(--continueButtonColor)',
        boxShadow: 'none',
        margin: '40px auto',
        borderRadius: 0,
        display: 'inline-flex',
        justifyContent: 'center',
        textDecoration: 'none',
        '&:hover': {
            boxShadow: 'none',
            background: 'color-mix(in hsl, var(--continueButton) 85%, white)',
        },
        '&:disabled': {
            background: '#b1b1b1',
            color: '#868686',
        },
        // @ts-ignore
        [theme.breakpoints.down('xs')]: {
            position: 'sticky',
            bottom: '20px',
            maxWidth: '100%',
        }

    },
    input: {
        width: '100%',
        '& input': {
            padding: '12px 14px',
        },
        '& label': {
            fontSize: '16px',
        },
        '& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': {
            //transform: 'translate(14px, 14px) scale(1)',
        },
        '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
            color: 'inherit',
            fontSize: '16px',
            transform: 'translate(0px, -22px) scale(1)',
        }
    },
    textArea: {
        width: '100%',
        minHeight: '100px',
        marginTop: '0.5rem',
        padding: '0.5rem',
        fontFamily: 'inherit',
        fontSize: '1rem',
        border: 'none',
        resize: 'vertical'
    },
    datepickerWrapper: {
        margin: '10px 0 20px',
        width: '100%',
        fontWeight: 400,
        fontSize: '18px',
        fontFamily: 'Futura-pt !important',
        position: 'relative',
        '& .react-datepicker-popper': {
            width: '100%',
        },
        '& .react-datepicker': {
            width: '100%',
            fontFamily: 'Futura-pt !important',
        },
        '& .react-datepicker__month-container': {
            float: 'unset'
        },
        '& .react-datepicker__day': {
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: 'min(3rem, 8vw)',
            height: 'min(3rem, 8vw)',
        },
        '& .react-datepicker__day--selected, & .react-datepicker__day--keyboard-selected': {
            backgroundColor: '#c39c63',
            borderRadius: '50%',
        },
        '& .react-datepicker__navigation-icon::before': {
            top: '15px',
        }
    },
    datepicker: {
        width: '100%',
        padding: '15px',
        textAlign: 'center',
        letterSpacing: '2px',
        fontWeight: 400,
        fontSize: '16px',
        fontFamily: 'Futura-pt !important',
        border: 'none',
        borderRadius: '1px',
    },
    datepickerLabel: {
        textAlign: 'left',
        fontSize: '16px',
        display: 'block',
    },
    halfWidthWrapper: {
        width: '100%',
        display: 'flex',
    },
    halfWidth: {
        display: 'inline-block',
        margin: '0 5px 0',
        width: 'calc(50% - 5px)',
        '&:first-of-type': {
            marginLeft: 0,
        },
        '& + &': {
            marginRight: 0,
        }
    },
}, { name: 'create-wishlist-page' });


export default function CreateWishlistPage() {
    const user = useUserStore();
    const router = useRouter();
    const query = router.query;
    const [isBrowser, setIsBrowser] = useState<boolean>(false);
    const [numOfSteps, setNumOfSteps] = useState<number>(2);
    const [step, setStep] = useState<number>(1);
    const [dateLUT, setDateLUT] = useState<Date>(new Date());

    const allowedTypes: WishlistType[] = ['standard', 'baby', 'birthday', 'christmas', 'graduation', 'move', 'wedding'];
    const initialType: WishlistType =
        (typeof query.type === 'string' && allowedTypes.includes(query.type as WishlistType))
            ? (query.type as WishlistType)
            : 'standard';
    const [selectedCelebration, setSelectedCelebration] = useState<CreateWishlist>({
        name: '',
        type: initialType,
        text: '',
        location: '',
        date: '',
        meta: {},
    });
    const classes = useStyles();
    const { t } = useTranslation(['account']);
    const currentTheme = backgrounds[selectedCelebration.type] || { color: '#f1f1ed', image: 'none' };
    const {actions: trackingActions} = useContext(TrackingContext);
    const { toggleDrawer } = useApplicationStore((state) => state.methods);

    // Get current step from URL, default to 0 if not present
    const currentStep = router.isReady ? (parseInt(router.query.step as string, 10) || 0) : 0;

    useEffect(() => {
        trackingActions.pageImpression(`Create wishlist: ${selectedCelebration.type} - Steg ${step}`, 'create_wishlist');
    }, [step])

    useEffect(() => {
        //@ts-ignore
        if (!window?.__lazyHydrateNavigated) {
            router.push(
                {
                    pathname: router.pathname,
                    query: { ...router.query, step: 0 }
                },
                undefined,
                { scroll: false }
            );
        } else {
            const step = router.isReady ? (parseInt(router.query.step as string, 10) || 0) : 0;
            setStep(step)
        }
    }, [router])


    useEffect(() => {
        if (user.isLoggedIn && step === numOfSteps) {
            createWishlist();
        }
    }, [step, user.isLoggedIn]);


    useEffect(() => {
        switch (selectedCelebration.type) {
            case 'standard':
            case 'baby':
            case 'birthday':
            case 'christmas':
            case 'graduation':
            case 'move':
                setNumOfSteps(2);
                break;
            case 'wedding':
                setNumOfSteps(4);
                break;
        }

    }, [selectedCelebration.type]);


    const stepForward = () => {
        // Calculate new step based on current URL parameter
        const currentStep = router.query.step ? parseInt(router.query.step as string, 10) : 0;
        const newStep = currentStep + 1;

        // Only update URL, no state to change
        router.push(
            {
                pathname: router.pathname,
                query: { ...router.query, step: newStep }
            },
            undefined,
            { scroll: false }
        );
    }

    const stepBack = () => {
        // Calculate new step based on current URL parameter
        const currentStep = router.query.step ? parseInt(router.query.step as string, 10) : 0;
        const newStep = currentStep - 1;

        if (newStep <= 0) {
            // Only update URL, no state to change
            router.push(
                {
                    pathname: router.pathname
                },
                undefined,
                { scroll: false }
            );
        } else {
            // Only update URL, no state to change
            router.push(
                {
                    pathname: router.pathname,
                    query: { ...router.query, step: newStep }
                },
                undefined,
                { scroll: false }
            );
        }

    }
    const createWishlist = async () => {
        if (selectedCelebration) {
            const response = await user.wishlistMethods.createNewWishlist(selectedCelebration);
            if (response.status === 'success') {
                trackingActions.click('wishlist', 'created')
            }
        }
    }

    useEffect(() => {
        setIsBrowser(true);
    }, []);

    useEffect(() => {
        let date = ''
        if (dateLUT.getDate() !== new Date().getDate()) {
            date = dateLUT.toISOString().slice(0, 10)
        }
        setSelectedCelebration({ ...selectedCelebration, date: date })
    }, [dateLUT]);

    if (!isBrowser) {
        return null;
    }

    return (
        <>
        <MetaTitle metaTitle={`Newport - Skapa önskelista: ${selectedCelebration.type} - Steg ${step}`} title={`Newport - Skapa önskelista: ${selectedCelebration.type} - Steg ${step}` || "Create wishlist | Newport"} />
        <Container
            disableGutters
            className={classes.page}
            style={{
                backgroundColor: currentTheme.background,
                color: currentTheme.color,
                //@ts-ignore
                '--flower': currentTheme.image,
            }}
        >
            <Container disableGutters className={classes.pageContent}>
                <div className={classes.pageControls}>
                    <Button className={currentStep > 0 ? 'show' : ''} variant="text" onClick={stepBack}>←</Button>
                    <p className={currentStep > 0 ? 'show' : ''}>{currentStep} / {numOfSteps}</p>
                    <Button variant="text">→</Button>
                </div>
                    {(currentStep === 0 || !router.query.step) &&
                        <CelebrationSelector
                            onChange={(value:WishlistType)=> {
                                if (value === 'wedding') {
                                    setSelectedCelebration({...selectedCelebration, type: value, name: t('account:wishlist-theme-wedding')})
                                } else {
                                    setSelectedCelebration({...selectedCelebration, type: value, name: ''});
                                }
                            }}
                            onContinue={stepForward}
                            currentSelected={selectedCelebration}
                        ></CelebrationSelector>
                    }
                    {currentStep >= 1 && currentStep !== numOfSteps &&
                        <>
                            { (selectedCelebration.type === 'standard' || selectedCelebration.type === 'christmas' || selectedCelebration.type === 'move') &&
                                <StandardWishlistConfigurator
                                    currentSelected={selectedCelebration}
                                    onChange={setSelectedCelebration}
                                    onContinue={()=> {
                                        stepForward()
                                    }}
                                    step={currentStep}
                                ></StandardWishlistConfigurator>
                            }

                            { (selectedCelebration.type === 'birthday' || selectedCelebration.type === 'baby' || selectedCelebration.type === 'graduation')  &&
                                <DateWishlistConfigurator
                                    currentSelected={selectedCelebration}
                                    onChange={setSelectedCelebration}
                                    onDateChange={setDateLUT}
                                    onContinue={()=> {
                                        stepForward()
                                    }}
                                    step={currentStep}
                                ></DateWishlistConfigurator>
                            }
                            { (selectedCelebration.type === 'wedding')  &&
                                <WeddingWishlistConfigurator
                                    currentSelected={selectedCelebration}
                                    onChange={setSelectedCelebration}
                                    onDateChange={setDateLUT}
                                    onContinue={()=> {
                                        stepForward()
                                    }}
                                    step={currentStep}
                                ></WeddingWishlistConfigurator>
                            }
                        </>
                    }
                    {currentStep === numOfSteps &&
                        <Box className={classes.wrapper}>
                            {user.isLoggedIn ?
                                <>
                                    <Typography variant="h6" className={classes.title}>
                                        {t('wishlist-wizard-outro-loggedin-title')}
                                    </Typography>
                                    <Typography variant="body1" paragraph style={{ margin: '20px 0' }}>
                                        {t('wishlist-wizard-outro-loggedin-description')}
                                    </Typography>

                                    <Link href={selectedCelebration.type === 'christmas' ? "/create-christmas-list" : "/create-wishlist"}>
                                        <a href={selectedCelebration.type === 'christmas' ? "/create-christmas-list" : "/create-wishlist"}
                                           className={classes.continueButton}>
                                            {t('wishlist-wizard-outro-loggedin-cta')}
                                        </a>
                                    </Link>
                                </>
                                :
                                <>
                                    <Typography variant="h6" className={classes.title}>
                                        {t('wishlist-wizard-outro-loggedout-title')}
                                    </Typography>
                                    <Typography variant="body1" paragraph style={{ margin: '20px 0' }}>
                                        {t('wishlist-wizard-outro-loggedout-description')}
                                    </Typography>

                                    <Button className={classes.continueButton} onClick={()=>{toggleDrawer('accountDrawerOpen')}}>
                                        {t('wishlist-wizard-outro-loggedout-cta')}
                                    </Button>
                                </>

                            }

                        </Box>
                    }
            </Container>
        </Container>
        </>
    );
}

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
    const appData = await appDataCache(context);
    return {
        props: {
            appData,
        },
    };
};
